/**
 * Merge puro do resultado de calcularMetasColaboradores com o estado atual
 * de form.metasCalculadas, e registro da edicao manual de uma meta.
 *
 * Extraido do TabelaMetasCalculadas.vue para ser testado sem precisar montar
 * o componente (Vuetify + v-simple-table + v-text-field no Jest).
 */

/**
 * Combina o resultado do recalculo com o estado atual, preservando metas
 * marcadas como manual (so a media e atualizada nesse caso).
 * @param {Object.<number, {media:number, meta:number, piso_aplicado:boolean, manual:boolean}>} atual
 * @param {Array<{id_colaborador:number, media:number, meta_calculada:number, piso_aplicado:boolean}>} calculadas
 * @returns {Object.<number, {media:number, meta:number, piso_aplicado:boolean, manual:boolean}>}
 */
export function mesclarMetasCalculadas(atual, calculadas) {
  const base = atual || {};
  const novo = {};
  (calculadas || []).forEach(item => {
    const anterior = base[item.id_colaborador];
    if (anterior && anterior.manual) {
      novo[item.id_colaborador] = { ...anterior, media: item.media };
      return;
    }
    novo[item.id_colaborador] = {
      media: item.media,
      meta: item.meta_calculada,
      piso_aplicado: item.piso_aplicado,
      manual: false,
      // bonusGravado nao e recalculado por aqui — e o valor de bonus que veio do
      // banco, e sobrevive ao recalculo da META porque as duas coisas nao tem
      // relacao. Perder ele aqui reabriria a perda de dado que ele existe para
      // impedir: bastaria clicar Recalcular e salvar para apagar o bonus
      // gravado. Ver montarMetasCalculadas e montarExtrasMeta.
      bonusGravado: anterior ? anterior.bonusGravado : undefined
    };
  });
  return novo;
}

/**
 * Registra a edicao manual da meta de um colaborador, marcando `manual: true`.
 * Nao mexe em `media` nem `piso_aplicado` do registro anterior.
 * @param {Object.<number, {media:number, meta:number, piso_aplicado:boolean, manual:boolean}>} atual
 * @param {number} idColaborador
 * @param {string|number} valor valor digitado no campo (pode vir vazio)
 * @returns {Object.<number, {media:number, meta:number, piso_aplicado:boolean, manual:boolean}>}
 */
export function registrarEdicaoManual(atual, idColaborador, valor) {
  const base = atual || {};
  const anterior = base[idColaborador] || { media: 0, piso_aplicado: false };
  return {
    ...base,
    [idColaborador]: {
      ...anterior,
      meta: valor === "" ? "" : parseFloat(valor),
      manual: true
    }
  };
}

/**
 * Desfaz a edicao manual da meta de UM colaborador, devolvendo o valor que o
 * calculo por media historica produz hoje (media, meta e piso do parametro
 * `calculadas`) e limpando a marca `manual`.
 *
 * Existe porque `mesclarMetasCalculadas` protege as metas manuais do
 * Recalcular — o que e a regra certa, mas deixava a edicao a mao sem volta:
 * nao havia como saber qual seria a meta calculada sem apagar a manual de
 * todos. Aqui a volta e explicita e por linha.
 *
 * Sem entrada correspondente em `calculadas` (colaborador sem historico nos
 * meses de referencia) devolve `null` — nao ha meta calculada pra onde
 * voltar, e sobrescrever com zero apagaria a meta do participante. Quem chama
 * avisa em vez de aplicar.
 *
 * @param {Object.<number, {media:number, meta:number, piso_aplicado:boolean, manual:boolean}>} atual
 * @param {number} idColaborador
 * @param {Array<{id_colaborador:number, media:number, meta_calculada:number, piso_aplicado:boolean}>} calculadas
 * @returns {Object.<number, {media:number, meta:number, piso_aplicado:boolean, manual:boolean}>|null}
 */
export function reverterEdicaoManual(atual, idColaborador, calculadas) {
  const base = atual || {};
  const item = (calculadas || []).find(
    c => String(c.id_colaborador) === String(idColaborador)
  );
  if (!item) return null;
  const anterior = base[idColaborador];
  return {
    ...base,
    [idColaborador]: {
      media: item.media,
      meta: item.meta_calculada,
      piso_aplicado: item.piso_aplicado,
      manual: false,
      // Mesmo motivo de mesclarMetasCalculadas: reverter a META a mao nao tem
      // nada a ver com o bonus gravado, e perde-lo aqui apagaria o valor na
      // proxima gravacao.
      bonusGravado: anterior ? anterior.bonusGravado : undefined
    }
  };
}
